/**
 * Service API DUERP V1
 * Module d'aide à la structuration du Document Unique
 *
 * IMPORTANT: Ce module est un AGRÉGATEUR/STRUCTURATEUR de données.
 * Termes interdits: calcul, diagnostic, conformité, validation, certification
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

// =====================================================
// TYPES
// =====================================================

export interface DuerpDocument {
  id: string
  document_id: string
  raison_sociale: string
  siret?: string
  adresse: string
  code_postal?: string
  ville?: string
  effectif_total?: number
  code_naf?: string
  secteur_activite?: string
  responsable_nom?: string
  responsable_fonction?: string
  user_id: number
  tool_version: string
  created_at: string
  updated_at: string
  nb_unites?: number
  nb_substances?: number
}

export interface DuerpUnite {
  id: string
  duerp_id: string
  nom: string
  description?: string
  effectif?: number
  ordre: number
  created_at: string
  updated_at: string
  nb_substances?: number
  nb_cmr?: number
  substances?: DuerpSubstance[]
}

export interface DuerpSubstance {
  id: string
  unite_id: string
  cas_number: string
  nom_substance?: string
  activite_tache?: string
  frequence?: 'occasionnelle' | 'hebdomadaire' | 'quotidienne' | 'permanente'
  duree_moyenne?: string
  forme?: 'poudre' | 'liquide' | 'vapeurs' | 'gaz' | 'solide' | 'autre'
  procedes?: string
  voies_exposition?: string[]
  epc_existants?: string[]
  epi_portes?: string[]
  epi_details?: string
  mesures_prevention?: string
  observations?: string
  donnees_reglementaires?: DonneesReglementaires
  date_consultation: string
  ordre: number
  created_at: string
  updated_at: string
}

/**
 * Cache d'affichage des données issues des sources officielles via RISK-DATA.
 *
 * ATTENTION: Ces données sont fournies à titre purement informatif, sans
 * garantie d'exhaustivité, d'exactitude absolue ni d'instantanéité.
 * La fiche substance RISK-DATA demeure la source de consultation de référence.
 * Aucune stabilité historique n'est garantie.
 */
export interface DonneesReglementaires {
  est_cmr?: boolean
  categorie_cmr?: string
  codes_h?: string[]
  pictogrammes?: string[]
  vlep_8h?: { valeur: number; unite: string }
  vlep_court_terme?: { valeur: number; unite: string }
  epi_recommandes?: string[]
  sources?: string[]
}

export interface DuerpDocumentFull extends DuerpDocument {
  unites: DuerpUnite[]
}

export interface DuerpExport {
  export_id: string
  generated_at: string
  tool_version: string
  document: DuerpDocumentFull
  synthese: {
    nb_unites: number
    nb_substances: number
    nb_cmr: number
    presence_cmr: boolean
  }
  mentions_legales: {
    titre: string
    usage_pro: string
    disclaimer: string
    donnees_sources: string
    multi_substances: string
  }
}

// =====================================================
// FETCH WRAPPER
// =====================================================

async function fetchDuerpAPI<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE}/api/duerp${endpoint}`

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
    ...options,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))

    if (response.status === 401) {
      throw new Error('Authentification requise pour accéder au module DUERP')
    }

    // Extraction robuste du message d'erreur
    let errorMessage = `Erreur HTTP ${response.status}`
    if (errorData.error) {
      errorMessage = typeof errorData.error === 'string'
        ? errorData.error
        : errorData.error.message || JSON.stringify(errorData.error)
    }

    throw new Error(errorMessage)
  }

  return response.json()
}

// =====================================================
// API DOCUMENTS DUERP
// =====================================================

export const duerpAPI = {
  /**
   * Liste des DUERP de l'utilisateur
   */
  list: async (): Promise<{ success: boolean; documents: DuerpDocument[]; count: number }> => {
    return fetchDuerpAPI('')
  },

  /**
   * Créer un nouveau DUERP
   */
  create: async (data: {
    raison_sociale: string
    siret?: string
    adresse: string
    code_postal?: string
    ville?: string
    effectif_total?: number
    code_naf?: string
    secteur_activite?: string
    responsable_nom?: string
    responsable_fonction?: string
  }): Promise<{ success: boolean; document: DuerpDocument }> => {
    return fetchDuerpAPI('', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Récupérer un DUERP avec ses unités et substances
   */
  get: async (id: string): Promise<{ success: boolean; document: DuerpDocumentFull }> => {
    return fetchDuerpAPI(`/${id}`)
  },

  /**
   * Mettre à jour les infos d'un DUERP
   */
  update: async (
    id: string,
    data: Partial<{
      raison_sociale: string
      siret: string
      adresse: string
      code_postal: string
      ville: string
      effectif_total: number
      code_naf: string
      secteur_activite: string
      responsable_nom: string
      responsable_fonction: string
    }>
  ): Promise<{ success: boolean; document: DuerpDocument }> => {
    return fetchDuerpAPI(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  /**
   * Supprimer un DUERP
   */
  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    return fetchDuerpAPI(`/${id}`, {
      method: 'DELETE',
    })
  },

  /**
   * Récupérer les données pour export PDF
   */
  getExport: async (id: string): Promise<{ success: boolean; export: DuerpExport }> => {
    return fetchDuerpAPI(`/${id}/export`)
  },
}

// =====================================================
// API UNITÉS DE TRAVAIL
// =====================================================

export const unitesAPI = {
  /**
   * Ajouter une unité de travail
   */
  create: async (
    duerpId: string,
    data: { nom: string; description?: string; effectif?: number }
  ): Promise<{ success: boolean; unite: DuerpUnite }> => {
    return fetchDuerpAPI(`/${duerpId}/unites`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Modifier une unité
   */
  update: async (
    duerpId: string,
    uniteId: string,
    data: Partial<{ nom: string; description: string; effectif: number }>
  ): Promise<{ success: boolean; unite: DuerpUnite }> => {
    return fetchDuerpAPI(`/${duerpId}/unites/${uniteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  /**
   * Supprimer une unité
   */
  delete: async (
    duerpId: string,
    uniteId: string
  ): Promise<{ success: boolean; message: string }> => {
    return fetchDuerpAPI(`/${duerpId}/unites/${uniteId}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// API SUBSTANCES
// =====================================================

export const substancesAPI = {
  /**
   * Ajouter une substance à une unité
   */
  create: async (
    duerpId: string,
    uniteId: string,
    data: {
      cas_number: string
      nom_substance?: string
      activite_tache?: string
      frequence?: string
      duree_moyenne?: string
      voies_exposition?: string[]
      epc_existants?: string[]
      epi_portes?: string[]
      epi_details?: string
      mesures_prevention?: string
      observations?: string
      donnees_reglementaires?: DonneesReglementaires
    }
  ): Promise<{ success: boolean; substance: DuerpSubstance }> => {
    return fetchDuerpAPI(`/${duerpId}/unites/${uniteId}/substances`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Modifier le contexte d'exposition d'une substance
   */
  update: async (
    duerpId: string,
    uniteId: string,
    substanceId: string,
    data: Partial<{
      activite_tache: string
      frequence: string
      duree_moyenne: string
      forme: string
      procedes: string
      voies_exposition: string[]
      epc_existants: string[]
      epi_portes: string[]
      epi_details: string
      mesures_prevention: string
      observations: string
    }>
  ): Promise<{ success: boolean; substance: DuerpSubstance }> => {
    return fetchDuerpAPI(`/${duerpId}/unites/${uniteId}/substances/${substanceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  /**
   * Retirer une substance
   */
  delete: async (
    duerpId: string,
    uniteId: string,
    substanceId: string
  ): Promise<{ success: boolean; message: string }> => {
    return fetchDuerpAPI(`/${duerpId}/unites/${uniteId}/substances/${substanceId}`, {
      method: 'DELETE',
    })
  },
}

// =====================================================
// CONSTANTES POUR LES FORMULAIRES
// =====================================================

export const FREQUENCE_OPTIONS = [
  { value: 'occasionnelle', label: 'Occasionnelle (< 1x/mois)' },
  { value: 'hebdomadaire', label: 'Hebdomadaire' },
  { value: 'quotidienne', label: 'Quotidienne' },
  { value: 'permanente', label: 'Permanente (continue)' },
] as const

export const FORME_OPTIONS = [
  { value: 'poudre', label: 'Poudre' },
  { value: 'liquide', label: 'Liquide' },
  { value: 'vapeurs', label: 'Vapeurs / Aérosols' },
  { value: 'gaz', label: 'Gaz' },
  { value: 'solide', label: 'Solide' },
  { value: 'autre', label: 'Autre' },
] as const

export const VOIES_EXPOSITION_OPTIONS = [
  { value: 'inhalation', label: 'Inhalation' },
  { value: 'cutanee', label: 'Contact cutané' },
  { value: 'oculaire', label: 'Contact oculaire' },
  { value: 'ingestion', label: 'Ingestion accidentelle' },
] as const

export const EPC_OPTIONS = [
  { value: 'ventilation_generale', label: 'Ventilation générale' },
  { value: 'captage_source', label: 'Captage à la source' },
  { value: 'vase_clos', label: 'Travail en vase clos' },
  { value: 'aucun', label: 'Aucun' },
] as const

export const EPI_OPTIONS = [
  { value: 'masque_ffp2', label: 'Masque FFP2' },
  { value: 'masque_ffp3', label: 'Masque FFP3' },
  { value: 'masque_a2', label: 'Masque à cartouche A2' },
  { value: 'masque_abek', label: 'Masque ABEK' },
  { value: 'gants_nitrile', label: 'Gants nitrile' },
  { value: 'gants_butyle', label: 'Gants butyle' },
  { value: 'gants_neoprene', label: 'Gants néoprène' },
  { value: 'lunettes', label: 'Lunettes de protection' },
  { value: 'ecran_facial', label: 'Écran facial' },
  { value: 'combinaison', label: 'Combinaison de protection' },
  { value: 'tablier', label: 'Tablier' },
  { value: 'aucun', label: 'Aucun' },
] as const

export const SECTEUR_OPTIONS = [
  { value: 'industrie_chimique', label: 'Industrie chimique' },
  { value: 'industrie_pharmaceutique', label: 'Industrie pharmaceutique' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'agroalimentaire', label: 'Agroalimentaire' },
  { value: 'btp', label: 'BTP' },
  { value: 'metallurgie', label: 'Métallurgie' },
  { value: 'plasturgie', label: 'Plasturgie' },
  { value: 'traitement_surface', label: 'Traitement de surface' },
  { value: 'peinture', label: 'Peinture / Revêtement' },
  { value: 'nettoyage', label: 'Nettoyage industriel' },
  { value: 'laboratoire', label: 'Laboratoire' },
  { value: 'sante', label: 'Santé' },
  { value: 'autre', label: 'Autre' },
] as const
